import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { MapOperationsService } from '@app/pages/weather/pages/gis-weather/services/map-operations.service';
import { DEFAULT_MARKER_CLUSTER_OPTIONS } from '@components/openstreetmap/constants/marker-cluster.constants';

declare var L: any;

interface MapClickEvent {
  lat: number;
  lng: number;
  type?: string;
  bounds?: any;
  points?: any;
  ctrlKey?: boolean;
  polygonData?: any;
  originalEvent?: any;
  containerPoint?: any;
  clientX?: number;
  clientY?: number;
}

interface MarkerClickEvent {
  id: string;
  lat: number;
  lng: number;
  data?: any;
}

interface Marker {
  id: string;
  lat: number;
  lng: number;
  title?: string;
  popup?: string;
  icon?: string;
  data?: any;
  isSelected?: boolean;
  autoOpenPopup?: boolean;
}

@Component({
  selector: 'app-leaflet-map',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './leaflet-map.component.html',
  styleUrls: ['./leaflet-map.component.scss']
})
export class LeafletMapComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef;
  private translate = inject(TranslateService);

  @Input() height: string = '400px';
  @Input() center: [number, number] = [environment.map.lat, environment.map.lng];
  @Input() zoom: number = 13;
  @Input() markers: Marker[] = [];
  @Input() drawingMode: boolean = false;
  @Input() showDrawingControls: boolean = true;
  @Input() selectedRegionBoundary: any = null;
  private rectangleDrawingEnabled: boolean = false;
  private polylineDrawingEnabled: boolean = false;
  private currentPolylinePoints: any[] = [];
  private currentPolylineMarkers: any[] = [];
  private currentPolyline: any = null;
  private zoomDependentMarkers: any[] = [];
  private zoomThresholdForPolygons = 13;
  private currentRectangle: any = null;
  private isDrawingRectangle: boolean = false;
  private isDrawingPolygon: boolean = false;
  private drawnItems: any;
  @Input() showControls: boolean = true;
  @Input() minimalControls: boolean = false;
  @Input() showMonitoringControl: boolean = true;
  @Input() showBaseLayerToggle: boolean = true;
  @Input() baseMap: 'osm' | 'landsat' | 'sentinel' = 'landsat';
  @Input() showRegionNames: boolean = true;
  @Input() disableBounds: boolean = false;
  @Input() clusteringEnabled: boolean = true;

  @Output() mapClick = new EventEmitter<MapClickEvent>();
  @Output() mapDblClick = new EventEmitter<MapClickEvent>();
  @Output() mapRightClick = new EventEmitter<MapClickEvent>();
  @Output() markerClick = new EventEmitter<MarkerClickEvent>();
  @Output() coordinatesChange = new EventEmitter<[number, number]>();
  @Output() mapMove = new EventEmitter<any>();
  @Output() monitoringPointsClick = new EventEmitter<any>();
  @Output() shapesChanged = new EventEmitter<any[]>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() mapMouseMove = new EventEmitter<{lat: number, lng: number}>();
  @Output() mapZoomChange = new EventEmitter<number>();

  public map: any;
  private markersLayer: any;
  private markerClusterGroup: any;
  private markerDictionary: Record<string, any> = {};
  private satelliteLayer: any;
  private ndviLayer: any;
  private indexLayers: Record<string, any> = {};
  private canvasRenderer: any;
  private dynamicPolygonsLayer: any;
  private regionBoundaryLayer: any;
  private polygonCountMarkers: any[] = [];
  private maxVisiblePolygons = 10;
  private clusterZoomThreshold = 12;
  private polygonClusters: Map<string, any> = new Map();
  private regionHighlightLayer: any;
  private currentHighlightedRegion: any = null;
  private switchedToOSM = false;
  private isEnforcingConstraints = false;
  private allPolygons: any[] = [];
  private isBulkAddingPolygons: boolean = false;
  isMapInitialized: boolean = false;
  isSatelliteLayerVisible: boolean = false;
  isNdviLayerVisible: boolean = false;
  private currentBaseLayer: any = null;
  private osmOverlayLayer: any = null;

  private tooltipButtons: { element: HTMLElement; key: string; }[] = [];
  private leafletZoomControl: any = null;
  private viewToggleButtons: { schemaBtn?: HTMLElement, satelliteBtn?: HTMLElement } = {};

  constructor(
    private mapOperationsService: MapOperationsService
  ) {
  }

  ngOnInit(): void {
     this.setupKeyboardHandlers();
    this.translate.onLangChange.subscribe((event) => {
      this.updateTooltips();
      this.updatePolygonTooltips();
    });
  }

  private updateTooltips(): void {
    this.tooltipButtons.forEach(({ element, key }) => {
      element.setAttribute('title', this.translate.instant(key));
    });

    if (this.viewToggleButtons.schemaBtn) {
      this.viewToggleButtons.schemaBtn.innerHTML = this.translate.instant('MAP_VIEW');
    }
    if (this.viewToggleButtons.satelliteBtn) {
      this.viewToggleButtons.satelliteBtn.innerHTML = this.translate.instant('SATELLITE_VIEW');
    }

    this.updateLeafletZoomControlTitles();
  }

  private updateLeafletZoomControlTitles(): void {
    if (!this.leafletZoomControl) {
      return;
    }
    const zoomInTitle = this.translate.instant('Zoom In');
    const zoomOutTitle = this.translate.instant('Zoom Out');
    if (this.leafletZoomControl._zoomInButton) {
      this.leafletZoomControl._zoomInButton.title = zoomInTitle;
      this.leafletZoomControl._zoomInButton.setAttribute('aria-label', zoomInTitle);
    }
    if (this.leafletZoomControl._zoomOutButton) {
      this.leafletZoomControl._zoomOutButton.title = zoomOutTitle;
      this.leafletZoomControl._zoomOutButton.setAttribute('aria-label', zoomOutTitle);
    }
  }

  private getTranslatedRegionName(properties: any): string {
    if (!properties) return '';

    const currentLang = this.translate.currentLang || 'ru';

    let nameEn, nameRu, nameUz, defaultName;

    if (properties.names) {
      nameEn = properties.names['en-US'] || properties.names['en'];
      nameRu = properties.names['ru-RU'] || properties.names['ru'];
      nameUz = properties.names['uz-Latn-UZ'] || properties.names['uz'];
      defaultName = properties.name || properties.title || '';
    } else {
      nameEn = properties.nameEn;
      nameRu = properties.nameRu;
      nameUz = properties.nameUz;
      defaultName = properties.name || '';
    }

    let result;
    if (currentLang === 'en' || currentLang === 'en-us' || currentLang === 'en-US') {
      result = nameEn || nameRu || nameUz || defaultName || '';
    } else if (currentLang === 'uz' || currentLang === 'uz-latn' || currentLang === 'uz-Latn-UZ') {
      result = nameUz || nameRu || nameEn || defaultName || '';
    } else {
      result = nameRu || nameEn || nameUz || defaultName || '';
    }

    return result;
  }

  private setupKeyboardHandlers(): void {
    document.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (this.polylineDrawingEnabled) {
          event.preventDefault();
          if (this.currentPolylinePoints.length > 0) {
            this.cancelCurrentPolyline();
          }
          this.removeDrawingHelp();

          this.polylineDrawingEnabled = false;
          this.map.getContainer().style.cursor = '';
          this.disableDrawing();

          const polylineBtn = document.querySelector('.polyline-btn');
          if (polylineBtn) {
            polylineBtn.classList.remove('active');
          }

        }
        if (this.rectangleDrawingEnabled && this.isDrawingRectangle) {
          event.preventDefault();
          this.cancelRectangleDrawing();
          this.removeRectangleHelp();

          this.rectangleDrawingEnabled = false;
          this.map.getContainer().style.cursor = '';
          this.disableDrawing();

          const rectangleBtn = document.querySelector('.rectangle-btn');
          if (rectangleBtn) {
            rectangleBtn.classList.remove('active');
          }

        }
      }

      if ((event.ctrlKey || event.metaKey) && event.key === 'z') {
        if (this.polylineDrawingEnabled && this.currentPolylinePoints.length > 0) {
          event.preventDefault();
          this.removeLastPolylinePoint();
        }
      }
    });
  }

  private removeLastPolylinePoint(): void {
    if (this.currentPolylinePoints.length === 0) return;

    this.currentPolylinePoints.pop();

    if (this.currentPolylineMarkers.length > 0) {
      const lastMarker = this.currentPolylineMarkers.pop();
      if (lastMarker) {
        this.map.removeLayer(lastMarker);
      }
    }

    if (this.currentPolyline) {
      if (this.currentPolylinePoints.length >= 2) {
        const closedPoints = [...this.currentPolylinePoints, this.currentPolylinePoints[0]];
        this.currentPolyline.setLatLngs(closedPoints);
      } else {
        this.map.removeLayer(this.currentPolyline);
        this.currentPolyline = null;
      }
    }

    this.showPolylineHelp();
  }

  ngAfterViewInit(): void {
    if (typeof L !== 'undefined') {
      setTimeout(() => {
        this.initializeMap();
      }, 100);
    } else {
      this.loadLeafletFromCDN();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['markers'] && this.map && !changes['markers'].firstChange) {
       this.addMarkers();
    }
    if (changes['baseMap'] && this.map && !changes['baseMap'].firstChange) {
      this.applyBaseLayer();
    }

    if (changes['showRegionNames'] && this.map) {
      this.updatePolygonTooltips();
    }

    if (changes['selectedRegionBoundary'] && this.map) {
      this.updateDrawingConstraints();
    }
  }

  private updatePolygonTooltips(): void {
    if (!this.allPolygons || this.allPolygons.length === 0) return;

    this.allPolygons.forEach(polygonData => {
      if (!polygonData || !polygonData.polygon || !polygonData.properties) {
        return;
      }

      const polygon = polygonData.polygon;
      const properties = polygonData.properties;

      try {
        if (properties.name && (properties.type === 'season' || properties.type === 'direct-season' || properties.type === 'season-field' || this.showRegionNames)) {
          const translatedName = properties.tooltip || this.getTranslatedRegionName(properties);
          if (polygon.getTooltip && polygon.getTooltip()) {
            polygon.setTooltipContent(translatedName);
          } else if (polygon.bindTooltip) {
            polygon.bindTooltip(translatedName, {
              permanent: false,
              direction: 'center',
              className: properties.type?.includes('season') ? 'season-tooltip' : 'region-tooltip',
              opacity: 0.9,
              sticky: true
            });
          }
        } else if (!properties.type?.includes('season') && !this.showRegionNames) {
          if (polygon.getTooltip && polygon.getTooltip() && polygon.unbindTooltip) {
            polygon.unbindTooltip();
          }
        }
      } catch (error) {
        console.warn('Error updating polygon tooltip:', error, polygonData);
      }
    });
  }

  private loadLeafletFromCDN(): void {
    const leafletCSS = document.createElement('link');
    leafletCSS.rel = 'stylesheet';
    leafletCSS.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(leafletCSS);

    const leafletJS = document.createElement('script');
    leafletJS.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    leafletJS.onload = () => {
      const clusterJS = document.createElement('script');
      clusterJS.src = 'https://unpkg.com/leaflet.markercluster@1.4.1/dist/leaflet.markercluster.js';
      clusterJS.onload = () => {
        setTimeout(() => {
          this.initializeMap();
        }, 100);
      };
      document.head.appendChild(clusterJS);
    };
    document.head.appendChild(leafletJS);

    const clusterCSS = document.createElement('link');
    clusterCSS.rel = 'stylesheet';
    clusterCSS.href = 'https://unpkg.com/leaflet.markercluster@1.4.1/dist/MarkerCluster.css';
    document.head.appendChild(clusterCSS);

    const clusterDefaultCSS = document.createElement('link');
    clusterDefaultCSS.rel = 'stylesheet';
    clusterDefaultCSS.href = 'https://unpkg.com/leaflet.markercluster@1.4.1/dist/MarkerCluster.Default.css';
    document.head.appendChild(clusterDefaultCSS);
      }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.remove();
    }
  }

  private initializeMap(): void {
    if (typeof L === 'undefined') {
      return;
    }

     const uzbekistanBounds = L.latLngBounds(
      [35.0, 55.5],
      [46.0, 74.0]
    );

    const mapOptions: any = {
      zoomControl: false,
      minZoom: 6,
      maxZoom: 19,
      fadeAnimation: true,
      zoomAnimation: true,
      zoomAnimationThreshold: 4,
      markerZoomAnimation: true
    };

    if (!this.disableBounds) {
      mapOptions.maxBounds = uzbekistanBounds;
      mapOptions.maxBoundsViscosity = 1.0;
    }

    this.map = L.map(this.mapContainer.nativeElement, mapOptions).setView(this.center, Math.max(this.zoom, 6));

    const osmTileUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
    const customTileUrl = `${environment.api}/tiles/Map/GetTile?lat={y}&lon={x}&z={z}&coordinateSystem=1`;

    const useOSM = true;

    const tileLayer = L.tileLayer(useOSM ? osmTileUrl : customTileUrl, {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
      minZoom: 6,
      updateWhenZooming: false,
      updateWhenIdle: true,
      keepBuffer: 4,
      noWrap: true,
      bounds: uzbekistanBounds,
      errorTileUrl: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=',
      tileSize: 256,
      zoomOffset: 0,
      detectRetina: true,
      crossOrigin: true
    });

    let tileErrorCount = 0;
    const maxTileErrors = 5;

    tileLayer.on('tileerror', (error: any) => {
      tileErrorCount++;
      console.warn('Tile loading error:', error.tile.src);

      if (tileErrorCount >= maxTileErrors && !this.switchedToOSM) {
         this.switchedToOSM = true;

        this.map.removeLayer(tileLayer);

        const osmLayer = L.tileLayer(osmTileUrl, {
          attribution: '© OpenStreetMap contributors',
          maxZoom: 19,
          minZoom: 6,
          updateWhenZooming: false,
          updateWhenIdle: true,
          keepBuffer: 2,
          noWrap: true,
          bounds: uzbekistanBounds
        }).addTo(this.map);
      }
    });

    tileLayer.addTo(this.map);

    const calculateMinimumZoom = () => {
      if (!this.map) return 6;

      const mapSize = this.map.getSize();

      const maxBoundsSize = {
        lat: uzbekistanBounds.getNorth() - uzbekistanBounds.getSouth(),
        lng: uzbekistanBounds.getEast() - uzbekistanBounds.getWest()
      };

      let minZoomLat = 6;
      let minZoomLng = 6;

      for (let z = 6; z <= 19; z++) {
        const degreesPerPixelLat = maxBoundsSize.lat / (256 * Math.pow(2, z));
        const degreesPerPixelLng = maxBoundsSize.lng / (256 * Math.pow(2, z));

        const viewportDegreesLat = degreesPerPixelLat * mapSize.y;
        const viewportDegreesLng = degreesPerPixelLng * mapSize.x;

        if (viewportDegreesLat <= maxBoundsSize.lat) {
          minZoomLat = z;
          break;
        }
      }

      for (let z = 6; z <= 19; z++) {
        const degreesPerPixelLng = maxBoundsSize.lng / (256 * Math.pow(2, z));
        const viewportDegreesLng = degreesPerPixelLng * mapSize.x;

        if (viewportDegreesLng <= maxBoundsSize.lng) {
          minZoomLng = z;
          break;
        }
      }

      return Math.max(minZoomLat, minZoomLng, 6);
    };

    const enforceViewportConstraints = () => {
      if (!this.map || this.isEnforcingConstraints) return;

      if (this.disableBounds) {
        return;
      }

      this.isEnforcingConstraints = true;

      const zoom = this.map.getZoom();
      const center = this.map.getCenter();
      const mapSize = this.map.getSize();

      const calculatedMinZoom = calculateMinimumZoom();

      if (calculatedMinZoom > zoom) {
        this.map.setMinZoom(calculatedMinZoom);
        this.map.setZoom(calculatedMinZoom, { animate: false });
        this.isEnforcingConstraints = false;
        return;
      }

      const bounds = this.map.getBounds();
      const viewportHeight = bounds.getNorth() - bounds.getSouth();
      const viewportWidth = bounds.getEast() - bounds.getWest();

      const maxLat = uzbekistanBounds.getNorth();
      const minLat = uzbekistanBounds.getSouth();
      const maxLng = uzbekistanBounds.getEast();
      const minLng = uzbekistanBounds.getWest();

      let newLat = center.lat;
      let newLng = center.lng;
      let needsUpdate = false;

      const halfHeight = viewportHeight / 2;
      if (center.lat + halfHeight > maxLat) {
        newLat = maxLat - halfHeight;
        needsUpdate = true;
      } else if (center.lat - halfHeight < minLat) {
        newLat = minLat + halfHeight;
        needsUpdate = true;
      }

      const halfWidth = viewportWidth / 2;
      if (center.lng + halfWidth > maxLng) {
        newLng = maxLng - halfWidth;
        needsUpdate = true;
      } else if (center.lng - halfWidth < minLng) {
        newLng = minLng + halfWidth;
        needsUpdate = true;
      }

      if (needsUpdate) {
        this.map.setView([newLat, newLng], zoom, { animate: false });
      }

      this.isEnforcingConstraints = false;
    };

    const initialMinZoom = calculateMinimumZoom();
    this.map.setMinZoom(initialMinZoom);

    this.map.on('moveend', enforceViewportConstraints);
    this.map.on('zoomend', enforceViewportConstraints);
    this.map.on('resize', () => {
      const newMinZoom = calculateMinimumZoom();
      this.map.setMinZoom(newMinZoom);
      enforceViewportConstraints();
    });

    if (window.ResizeObserver) {
      const resizeObserver = new ResizeObserver(() => {
        setTimeout(() => {
          const newMinZoom = calculateMinimumZoom();
          this.map.setMinZoom(newMinZoom);
          if (this.map.getZoom() < newMinZoom) {
            this.map.setZoom(newMinZoom, { animate: false });
          }
          enforceViewportConstraints();
        }, 100);
      });
      resizeObserver.observe(this.mapContainer.nativeElement);
    }

    setTimeout(() => {
      enforceViewportConstraints();
    }, 100);


    if (!this.map.getPane('markerPane')) {
      const markerPane = this.map.createPane('markerPane');
      markerPane.style.zIndex = 650;
    }

    this.markersLayer = L.layerGroup({ pane: 'markerPane' }).addTo(this.map);

    if (this.clusteringEnabled && typeof (L as any).markerClusterGroup !== 'undefined') {
      // Override shared `iconCreateFunction` (GIS monitoring clusters) — red cluster styling applies only on OpenStreetMap.
      this.markerClusterGroup = (L as any).markerClusterGroup({
        ...DEFAULT_MARKER_CLUSTER_OPTIONS,
        pane: 'markerPane',
        iconCreateFunction: function (cluster: any) {
          const count = cluster.getChildCount();
          let className = 'marker-cluster-small';

          if (count > 10) {
            className = 'marker-cluster-medium';
          }
          if (count > 50) {
            className = 'marker-cluster-large';
          }

          return new (L as any).DivIcon({
            html: '<div><span>' + count + '</span></div>',
            className: 'marker-cluster ' + className,
            iconSize: new (L as any).Point(40, 40)
          });
        }
      });
      this.map.addLayer(this.markerClusterGroup);
    }

    if (!this.map.getPane('drawnItemsPane')) {
      const drawnPane = this.map.createPane('drawnItemsPane');
      drawnPane.style.zIndex = 900;
    }

    this.canvasRenderer = L.canvas({ padding: 0.2 });
    this.applyBaseLayer();
    this.drawnItems = L.featureGroup().addTo(this.map);

    this.addMarkers();

    this.map.on('click', (e: any) => {
      const coords = e.latlng;
      this.mapClick.emit({
        lat: coords.lat,
        lng: coords.lng,
        ctrlKey: e.originalEvent?.ctrlKey || false
      });
      this.coordinatesChange.emit([coords.lat, coords.lng]);
    });

    this.map.on('contextmenu', (e: any) => {
      const coords = e.latlng;
      L.DomEvent.preventDefault(e);
      this.mapRightClick.emit({
        lat: coords.lat,
        lng: coords.lng,
        originalEvent: e.originalEvent,
        containerPoint: e.containerPoint,
        clientX: e.originalEvent?.clientX,
        clientY: e.originalEvent?.clientY
      });
    });

    this.map.on('dblclick', (e: any) => {
      if (this.rectangleDrawingEnabled || this.polylineDrawingEnabled) {
        return;
      }

      const coords = e.latlng;
      this.mapDblClick.emit({
        lat: coords.lat,
        lng: coords.lng
      });
    });

    this.map.on('moveend', () => {
      const bounds = this.getMapBounds();
      const center = this.map.getCenter();

      this.mapMove.emit({
        bounds,
        center: { lat: center.lat, lng: center.lng },
        zoom: this.map.getZoom()
      });

      this.updatePolygonVisibility();
    });

    this.map.on('zoomend', () => {
      this.updateZoomDependentMarkers();
      if (this.allPolygons.length > 0) {
        this.updatePolygonVisibility();
      }
      this.mapZoomChange.emit(this.map.getZoom());
    });

    this.map.on('moveend', () => {
      if (this.allPolygons.length > 0 && this.map.getZoom() < this.clusterZoomThreshold) {
        this.updatePolygonVisibility();
      }
    });

    this.map.on('mousemove', (e: any) => {
      this.mapMouseMove.emit({
        lat: e.latlng.lat,
        lng: e.latlng.lng
      });
    });

    this.updateZoomDependentMarkers();

    if (this.drawingMode) {
      this.initializeDrawingControls();
    } else {
      this.setupValidationEventListeners();
    }

    this.isMapInitialized = true;
    if (!this.minimalControls) {
      if (this.showBaseLayerToggle) {
        this.addCustomToggleControl();
      }
      this.addSearchControl();
      if (this.showMonitoringControl) {
        this.addMonitoringPointsControl();
      }
      this.leafletZoomControl = L.control.zoom({
        position: 'topright',
        zoomInTitle: this.translate.instant('Zoom In'),
        zoomOutTitle: this.translate.instant('Zoom Out'),
      }).addTo(this.map);
      if (this.showDrawingControls) {
        this.addTopLeftControls();
      }
    }
  }

  public addMarkers(): void {
     if (!this.markersLayer) {
      console.warn('markersLayer not available');
      return;
    }

    this.markersLayer.clearLayers();

    if (this.markerClusterGroup) {
      this.markerClusterGroup.clearLayers();
    }
    this.markerDictionary = {};

     this.markers.forEach(markerData => {
      const icon = markerData.icon
        ? L.divIcon({
            html: markerData.icon,
            className: 'custom-marker-icon',
            iconSize: [24, 24],
            iconAnchor: [12, 12],
            popupAnchor: [0, -12]
          })
        : this.createDefaultIcon(markerData.isSelected ?? false);

      const marker = L.marker([markerData.lat, markerData.lng], {
        title: markerData.title || '',
        icon,
        pane: 'markerPane'
      });

      if (markerData.popup) {
        marker.bindPopup(markerData.popup, {
          className: 'photo-marker-popup',
          autoPan: true,
          closeButton: true
        });

        if (markerData.isSelected || markerData.autoOpenPopup) {
          marker.once('add', () => marker.openPopup());
        }
      }

      marker.on('click', () => {
        this.markerClick.emit({
          id: markerData.id,
          lat: markerData.lat,
          lng: markerData.lng,
          data: markerData.data
        });
      });

      this.markerDictionary[markerData.id] = marker;

      if (this.markerClusterGroup) {
        this.markerClusterGroup.addLayer(marker);
      } else {
        marker.addTo(this.markersLayer);
      }
    });
  }

  private createDefaultIcon(selected: boolean): any {
    const fill = selected ? '#FF6B6B' : '#2196F3';
    const svgString = `
      <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="8" fill="${fill}" stroke="#ffffff" stroke-width="2"/>
        <circle cx="12" cy="12" r="3" fill="#ffffff"/>
      </svg>
    `;

    return L.icon({
      iconUrl: 'data:image/svg+xml;base64,' + btoa(svgString),
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      popupAnchor: [0, -12]
    });
  }

  private initializeDrawingControls(): void {
    if (typeof L.Control.Draw === 'undefined') {
       return;
    }


    const drawControl = new L.Control.Draw({
      edit: {
        featureGroup: this.drawnItems
      },
      draw: {
        polygon: true,
        polyline: true,
        rectangle: true,
        circle: true,
        marker: true
      }
    });

    this.map.addControl(drawControl);

    this.map.on('draw:drawstart', (e: any) => {
      this.mapOperationsService.updateDrawingState({
        isDrawing: true,
        drawingType: 'polygon'
      });

      if (e.layerType === 'polygon' || e.layerType === 'rectangle') {

        this.disablePolygonInteractions();

        this.map.eachLayer((layer: any) => {
          if (layer instanceof L.Polygon || layer instanceof L.Path) {
            if (layer.options && !layer.options.className?.includes('leaflet-draw')) {
              layer.options.interactive = false;
              if (layer._path) {
                layer._path.style.pointerEvents = 'none';
              }
            }
          }
        });

        this.map.getContainer().classList.add('drawing-mode-active');
      }
    });

    this.map.on('draw:drawstop', (e: any) => {
      this.mapOperationsService.updateDrawingState({
        isDrawing: false,
        drawingType: null
      });

      this.enablePolygonInteractions();

      this.map.eachLayer((layer: any) => {
        if (layer instanceof L.Polygon || layer instanceof L.Path) {
          if (layer.options && !layer.options.className?.includes('leaflet-draw')) {
            layer.options.interactive = true;
            if (layer._path) {
              layer._path.style.pointerEvents = '';
            }
          }
        }
      });

      this.map.getContainer().classList.remove('drawing-mode-active');
    });

    this.map.on(L.Draw.Event.CREATED, (e: any) => {
      const layer = e.layer;

      if (e.layerType === 'polygon' || e.layerType === 'rectangle') {
        if (this.checkForOverlapWithExistingPolygons(layer)) {
          alert('Error: Cannot create shape that overlaps with existing polygons. Please draw in a clear area.');
          return;
        }
      }

      this.drawnItems.addLayer(layer);
    });
  }

  private setupValidationEventListeners(): void {

     this.map.on(L.Draw.Event.CREATED, (e: any) => {
      const layer = e.layer;

       if (e.layerType === 'polygon' || e.layerType === 'rectangle') {
         if (this.checkForOverlapWithExistingPolygons(layer)) {
           alert('Error: Cannot create shape that overlaps with existing polygons. Please draw in a clear area.');
          return;
        }
       }

      this.drawnItems.addLayer(layer);
    });
  }

  private checkForOverlapWithExistingPolygons(newLayer: any): boolean {


     let newShapeCoords: any[] = [];

    if (newLayer.getBounds) {
       const bounds = newLayer.getBounds();
      newShapeCoords = [
        [bounds.getNorth(), bounds.getWest()],
        [bounds.getNorth(), bounds.getEast()],
        [bounds.getSouth(), bounds.getEast()],
        [bounds.getSouth(), bounds.getWest()]
      ];
     } else if (newLayer.getLatLngs) {
      const latLngs = newLayer.getLatLngs();
       newShapeCoords = Array.isArray(latLngs[0]) ? latLngs[0] : latLngs;
      newShapeCoords = newShapeCoords.map((point: any) => [point.lat, point.lng]);
     } else {
       return false;
    }

    if (newShapeCoords.length < 3) {
       return false;
    }

     let hasOverlap = false;
    let layerCount = 0;

      this.map.eachLayer((layer: any) => {
      layerCount++;
      if (layer !== newLayer && layer.getLatLngs && typeof layer.getLatLngs === 'function') {

         if (layer.options?.className === 'region-boundary-polygon' ||
            layer.options?.className === 'level3-polygon' ||
            layer.options?.pane === 'regionBoundaryPane' ||
            layer.options?.pane === 'level3BoundaryPane') {
           return;
        }

        let shouldCheck = false;
        if (this.drawnItems) {
          this.drawnItems.eachLayer((drawnLayer: any) => {
            if (drawnLayer === layer) {
              shouldCheck = true;
            }
          });
        }

        if (!shouldCheck) {
           return;
        }

        try {
          const existingLatLngs = layer.getLatLngs();
          let existingCoords = Array.isArray(existingLatLngs[0]) ? existingLatLngs[0] : existingLatLngs;

          if (existingCoords && existingCoords.length > 2) {
            existingCoords = existingCoords.map((point: any) => [point.lat, point.lng]);

            if (this.doPolygonsOverlap(newShapeCoords, existingCoords)) {
               hasOverlap = true;
            }
          }
        } catch (e) {
         }
      }
    });
    return hasOverlap;
  }

  private doPolygonsOverlap(poly1: any[], poly2: any[]): boolean {
    for (const point of poly1) {
      if (this.isPointInPolygon(point, poly2)) {
        return true;
      }
    }

    for (const point of poly2) {
      if (this.isPointInPolygon(point, poly1)) {
        return true;
      }
    }

    for (let i = 0; i < poly1.length; i++) {
      const edge1Start = poly1[i];
      const edge1End = poly1[(i + 1) % poly1.length];

      for (let j = 0; j < poly2.length; j++) {
        const edge2Start = poly2[j];
        const edge2End = poly2[(j + 1) % poly2.length];

        if (this.doLinesIntersect(edge1Start, edge1End, edge2Start, edge2End)) {
          return true;
        }
      }
    }

    return false;
  }

  private isPointInPolygon(point: any[], polygon: any[]): boolean {
    const x = point[0];
    const y = point[1];
    let inside = false;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i][0];
      const yi = polygon[i][1];
      const xj = polygon[j][0];
      const yj = polygon[j][1];

      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }

    return inside;
  }

  private doLinesIntersect(line1Start: any[], line1End: any[], line2Start: any[], line2End: any[]): boolean {
    const [x1, y1] = line1Start;
    const [x2, y2] = line1End;
    const [x3, y3] = line2Start;
    const [x4, y4] = line2End;

    const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);

    if (Math.abs(denom) < 1e-10) {
      return false;
    }

    const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
    const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;

    return t >= 0 && t <= 1 && u >= 0 && u <= 1;
  }

  private applyBaseLayer(): void {
    if (!this.map || typeof L === 'undefined') { return; }
    if (this.currentBaseLayer) {
      try { this.map.removeLayer(this.currentBaseLayer); } catch {}
      this.currentBaseLayer = null;
    }
    if (this.baseMap === 'osm') {
      this.currentBaseLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19
      });
    } else if (this.baseMap === 'sentinel') {
      this.currentBaseLayer = L.tileLayer(`${environment.api}/tiles/Map/GetTile?lat={y}&lon={x}&z={z}&layer=mosaics&coordinateSystem=1`, {
        maxZoom: 19,
        attribution: 'Sentinel Mosaics',
        opacity: 0.7
      });
    } else {
      this.currentBaseLayer = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        maxZoom: 19,
        attribution: 'Imagery © Esri'
      });
    }
    this.currentBaseLayer.addTo(this.map);
  }

  public setBaseMap(type: 'osm' | 'landsat' | 'sentinel'): void {
    this.baseMap = type;
    this.applyBaseLayer();
  }

  public addOSMOverlay(opacity: number = 0.3): void {
    if (!this.map || typeof L === 'undefined') {
      return;
    }

    this.removeOSMOverlay();

    this.osmOverlayLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
      opacity: opacity
    });

    this.osmOverlayLayer.addTo(this.map);
  }

  public removeOSMOverlay(): void {
    if (this.osmOverlayLayer && this.map) {
      try {
        this.map.removeLayer(this.osmOverlayLayer);
      } catch (e) {
        console.warn('Error removing OSM overlay:', e);
      }
      this.osmOverlayLayer = null;
    } else {
    }
  }

  public setOSMOverlayOpacity(opacity: number): void {
    if (this.osmOverlayLayer) {
      this.osmOverlayLayer.setOpacity(opacity);
    }
  }

  public setBaseLayerOpacity(opacity: number): void {
    if (this.currentBaseLayer) {
      this.currentBaseLayer.setOpacity(opacity);
    }
  }

  public setCenter(lat: number, lng: number, zoom?: number): void {

    if (this.map) {
      this.map.setView([lat, lng], zoom || this.zoom);
      console.log('🗺️ setView выполнен');
    } else {
      console.log('❌ map недоступен в setCenter');
    }
  }

  public openMarkerPopup(markerId: string): void {
    if (!this.map) {
      return;
    }

    const marker = this.markerDictionary[markerId];
    if (marker) {
      marker.openPopup();
    }
  }

  public panToMarker(markerId: string): void {
    if (!this.map) {
      return;
    }

    const marker = this.markerDictionary[markerId];
    if (marker) {
      const position = marker.getLatLng();
      this.map.panTo(position);
    }
  }

  public addMarker(marker: Marker): void {
    this.markers.push(marker);
    this.addMarkers();
  }

  public removeMarker(markerId: string): void {
    this.markers = this.markers.filter(m => m.id !== markerId);
    this.addMarkers();
  }

  public clearMarkers(): void {
    this.markers = [];
    if (this.markersLayer) {
      this.markersLayer.clearLayers();
    }
    if (this.markerClusterGroup) {
      this.markerClusterGroup.clearLayers();
    }
  }

  public clearMonitoringMarkers(): void {
    if (!this.markerClusterGroup) return;

    this.markerClusterGroup.eachLayer((layer: any) => {
      if (layer.options?.id?.startsWith('monitoring_')) {
        this.markerClusterGroup.removeLayer(layer);
      }
    });
  }

  public addPersistentMarker(markerData: Marker): any {
    if (!this.map || !L) return null;

    const svgString = `
      <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="8" fill="#2196F3" stroke="#ffffff" stroke-width="2"/>
        <circle cx="12" cy="12" r="3" fill="#ffffff"/>
      </svg>
    `;

    const svgBase64 = 'data:image/svg+xml;base64,' + btoa(svgString);

    const pointIcon = L.icon({
      iconUrl: svgBase64,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      popupAnchor: [0, -12]
    });

    const marker = L.marker([markerData.lat, markerData.lng], {
      title: markerData.title || '',
      icon: pointIcon,
      pane: 'markerPane',
      id: markerData.id
    });

    if (markerData.popup) {
      marker.bindPopup(markerData.popup);
    }

    marker.on('click', () => {
      this.markerClick.emit({
        id: markerData.id,
        lat: markerData.lat,
        lng: markerData.lng,
        data: markerData.data
      });
    });

    if (this.markerClusterGroup) {
      this.markerClusterGroup.addLayer(marker);
    } else {
      marker.addTo(this.map);
    }

    return marker;
  }

  public addZoomDependentMarker(marker: any): void {
    if (!this.map || !L) return;

    const polygonCoords = marker.polygonSize ?
      this.generatePolygonAroundPoint(marker.lat, marker.lng, marker.polygonSize) :
      null;

    this.zoomDependentMarkers.push({
      id: marker.id,
      lat: marker.lat,
      lng: marker.lng,
      title: marker.title,
      popup: marker.popup,
      polygonCoords: polygonCoords,
      markerLayer: null,
      polygonLayer: null,
      data: marker.data
    });

    this.updateZoomDependentMarkers();
  }

  private generatePolygonAroundPoint(lat: number, lng: number, size: number): [number, number][] {
    return [
      [lat - size, lng - size],
      [lat - size, lng + size],
      [lat + size, lng + size],
      [lat + size, lng - size],
      [lat - size, lng - size]
    ];
  }

  private updateZoomDependentMarkers(): void {
    if (!this.map || !L) return;

    const currentZoom = this.map.getZoom();

    this.zoomDependentMarkers.forEach(item => {
      if (currentZoom >= this.zoomThresholdForPolygons && item.polygonCoords) {
         if (item.markerLayer) {
          this.map.removeLayer(item.markerLayer);
          item.markerLayer = null;
        }

        if (!item.polygonLayer) {
          item.polygonLayer = L.polygon(item.polygonCoords, {
            color: '#2196F3',
            weight: 3,
            opacity: 1,
            fillColor: '#2196F3',
            fillOpacity: 0.5
          });



          item.polygonLayer.on('click', () => {
            this.markerClick.emit({
              id: item.id,
              lat: item.lat,
              lng: item.lng,
              data: item.data
            });
          });

          item.polygonLayer.addTo(this.map);
        }
      } else {
        if (item.polygonLayer) {
          this.map.removeLayer(item.polygonLayer);
          item.polygonLayer = null;
        }

        if (!item.markerLayer) {
          const svgString = `
            <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="8" fill="#2196F3" stroke="#ffffff" stroke-width="2"/>
              <circle cx="12" cy="12" r="3" fill="#ffffff"/>
            </svg>
          `;

          const svgBase64 = 'data:image/svg+xml;base64,' + btoa(svgString);
          const pointIcon = L.icon({
            iconUrl: svgBase64,
            iconSize: [24, 24],
            iconAnchor: [12, 12],
            popupAnchor: [0, -12]
          });

          item.markerLayer = L.marker([item.lat, item.lng], {
            title: item.title || '',
            icon: pointIcon,
            pane: 'markerPane'
          });

          item.markerLayer.on('click', () => {
            this.markerClick.emit({
              id: item.id,
              lat: item.lat,
              lng: item.lng,
              data: item.data
            });
          });

          item.markerLayer.addTo(this.map);
        }
      }
    });
  }

  public clearZoomDependentMarkers(): void {
    this.zoomDependentMarkers.forEach(item => {
      if (item.markerLayer) {
        this.map.removeLayer(item.markerLayer);
      }
      if (item.polygonLayer) {
        this.map.removeLayer(item.polygonLayer);
      }
    });
    this.zoomDependentMarkers = [];
  }

  public highlightMarker(markerId: string): void {
    if (!this.map || !L) return;

    const item = this.zoomDependentMarkers.find(m => m.id === markerId);
    if (item && item.markerLayer) {
      const marker = item.markerLayer as any;

      const pulseIcon = L.divIcon({
        className: 'pulse-marker',
        html: `
          <div style="position: relative;">
            <div class="pulse-ring"></div>
            <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="8" fill="#FF5722" stroke="#ffffff" stroke-width="2"/>
              <circle cx="12" cy="12" r="3" fill="#ffffff"/>
            </svg>
          </div>
        `,
        iconSize: [24, 24],
        iconAnchor: [12, 12],
        popupAnchor: [0, -12]
      });

      marker.setIcon(pulseIcon);

      const style = document.createElement('style');
      style.innerHTML = `
        @keyframes pulse {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.5);
            opacity: 0.5;
          }
          100% {
            transform: scale(2);
            opacity: 0;
          }
        }
        .pulse-ring {
          position: absolute;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          border: 3px solid #FF5722;
          animation: pulse 2s ease-out infinite;
        }
      `;
      document.head.appendChild(style);

      setTimeout(() => {
        const normalIcon = L.icon({
          iconUrl: 'data:image/svg+xml;base64,' + btoa(`
            <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="8" fill="#2196F3" stroke="#ffffff" stroke-width="2"/>
              <circle cx="12" cy="12" r="3" fill="#ffffff"/>
            </svg>
          `),
          iconSize: [24, 24],
          iconAnchor: [12, 12],
          popupAnchor: [0, -12]
        });
        marker.setIcon(normalIcon);
        document.head.removeChild(style);
      }, 3000);
    }
  }

  public fitBounds(markers?: Marker[]): void {
    const markersToFit = markers || this.markers;
    if (this.map && markersToFit.length > 0) {
      const bounds = L.latLngBounds(markersToFit.map(m => [m.lat, m.lng]));
      this.map.fitBounds(bounds, { padding: [50, 50] });
    }
  }

  public addSatelliteLayer(): void {
    if (!this.map || !L) return;

    if (!this.satelliteLayer) {
      this.satelliteLayer = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        attribution: '© Esri',
        maxZoom: 19,
        opacity: 0.5
      });

      this.satelliteLayer.addTo(this.map);
      this.isSatelliteLayerVisible = true;
    }
  }

  public removeSatelliteLayer(): void {
    if (this.map && this.satelliteLayer) {
      this.map.removeLayer(this.satelliteLayer);
      this.satelliteLayer = null;
      this.isSatelliteLayerVisible = false;
    }
  }

  private toggleRectangleDrawing(button: HTMLElement): void {

    if (this.polylineDrawingEnabled) {
      this.polylineDrawingEnabled = false;
      document.querySelector('.polyline-btn')?.classList.remove('active');
      this.cancelCurrentPolyline();
      this.removeDrawingHelp();
    }

    this.rectangleDrawingEnabled = !this.rectangleDrawingEnabled;
    button.classList.toggle('active');


    if (this.rectangleDrawingEnabled) {
      this.mapOperationsService.updateDrawingState({
        isDrawing: true,
        drawingType: 'polygon'
      });
      this.enableRectangleDrawing();
    } else {
      this.mapOperationsService.updateDrawingState({
        isDrawing: false,
        drawingType: null
      });
      this.map.getContainer().style.cursor = '';
      this.cancelRectangleDrawing();
      this.removeRectangleHelp();
      this.disableDrawing();
    }
  }

  private togglePolylineDrawing(button: HTMLElement): void {
    if (this.rectangleDrawingEnabled) {
      this.rectangleDrawingEnabled = false;
      document.querySelector('.rectangle-btn')?.classList.remove('active');
      this.cancelRectangleDrawing();
      this.removeRectangleHelp();
    }

    this.polylineDrawingEnabled = !this.polylineDrawingEnabled;
    button.classList.toggle('active');

    if (this.polylineDrawingEnabled) {
      this.mapOperationsService.updateDrawingState({
        isDrawing: true,
        drawingType: 'polygon'
      });
      this.enablePolylineDrawing();
    } else {
      this.mapOperationsService.updateDrawingState({
        isDrawing: false,
        drawingType: null
      });
      this.map.getContainer().style.cursor = '';
      this.cancelCurrentPolyline();
      this.removeDrawingHelp();
      this.disableDrawing();
    }
  }

  private enableRectangleDrawing(): void {
    this.map.off('click');
    this.map.off('mousemove');
    this.map.off('dblclick');


    this.disablePolygonInteractions();

    this.showRectangleDrawingHelp();

    this.map.getContainer().style.cursor = 'crosshair';

    this.map.on('click', (e: any) => {
      if (this.rectangleDrawingEnabled) {
        if (!this.isDrawingRectangle) {
          this.startDrawingRectangle(e.latlng);
          this.updateRectangleHelp();
        } else {
          this.finishRectangle();
        }
      }
    });

    this.map.on('mousemove', (e: any) => {
      if (this.rectangleDrawingEnabled) {
        this.updateDrawingCursor(e.latlng);

        if (this.isDrawingRectangle && this.currentRectangle) {
          this.updateRectangleSize(e.latlng);
        }
      }
    });
  }

  private showRectangleDrawingHelp(): void {
    const helpDiv = L.DomUtil.create('div', 'rectangle-drawing-help');
    helpDiv.style.cssText = `
      position: absolute;
      top: 60px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 8px 16px;
      border-radius: 4px;
      font-size: 14px;
      z-index: 1000;
      pointer-events: none;
    `;
    helpDiv.innerHTML = this.translate.instant('DRAW_RECTANGLE_HELP');

    this.map.getContainer().appendChild(helpDiv);
    (this as any).rectangleHelpDiv = helpDiv;
  }

  private updateRectangleHelp(): void {
    if ((this as any).rectangleHelpDiv) {
      (this as any).rectangleHelpDiv.innerHTML = this.translate.instant('DRAW_RECTANGLE_FINISH_HELP');
    }
  }

  private removeRectangleHelp(): void {
    if ((this as any).rectangleHelpDiv) {
      this.map.getContainer().removeChild((this as any).rectangleHelpDiv);
      (this as any).rectangleHelpDiv = null;
    }
  }

  private isRectangleInsideAnyRegion(bounds: any): boolean {
    const corners = [
      [bounds[0][0], bounds[0][1]],
      [bounds[0][0], bounds[1][1]],
      [bounds[1][0], bounds[0][1]],
      [bounds[1][0], bounds[1][1]]
    ];

    let foundContainingRegion = false;

    this.map.eachLayer((layer: any) => {
      if (layer instanceof L.Polygon &&
          (layer.options.className?.includes('level1-regionmap-polygon') ||
           layer.options.className?.includes('level1-polygon') ||
           layer.options.className?.includes('level2-polygon') ||
           layer.options.className?.includes('level3-polygon') ||
           layer.options.className?.includes('region-boundary-polygon'))) {

        let allCornersInside = true;
        for (const corner of corners) {
          const latLng = L.latLng(corner[0], corner[1]);
          if (!this.isPointInsidePolygon(latLng, layer)) {
            allCornersInside = false;
            break;
          }
        }

        if (allCornersInside) {
          foundContainingRegion = true;
        }
      }
    });

    return foundContainingRegion;
  }

  private isPointInsidePolygon(point: any, polygon: any): boolean {
    if (polygon.getBounds && polygon.getBounds().contains(point)) {
      const latlngs = polygon.getLatLngs()[0];
      let inside = false;

      for (let i = 0, j = latlngs.length - 1; i < latlngs.length; j = i++) {
        const xi = latlngs[i].lat, yi = latlngs[i].lng;
        const xj = latlngs[j].lat, yj = latlngs[j].lng;

        const intersect = ((yi > point.lng) != (yj > point.lng))
            && (point.lat < (xj - xi) * (point.lng - yi) / (yj - yi) + xi);
        if (intersect) inside = !inside;
      }

      return inside;
    }
    return false;
  }

  private showDrawingError(message: string): void {
    const errorDiv = L.DomUtil.create('div', 'drawing-error');
    errorDiv.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(244, 67, 54, 0.9);
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      font-size: 16px;
      z-index: 10000;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    `;
    errorDiv.innerHTML = `
      <div style="display: flex; align-items: center;">
        <span style="margin-right: 8px;">⚠️</span>
        <span>${message}</span>
      </div>
    `;

    this.map.getContainer().appendChild(errorDiv);

    setTimeout(() => {
      this.map.getContainer().removeChild(errorDiv);
    }, 3000);
  }

  private startDrawingRectangle(startPoint: any): void {
    this.isDrawingRectangle = true;

    this.currentRectangle = {
      startPoint: startPoint,
      layer: null
    };

    this.map.on('mousemove', (e: any) => {
      if (this.isDrawingRectangle && this.currentRectangle) {
        this.updateRectangleSize(e.latlng);
      }
    });
  }

  private updateRectangleSize(mousePos: any): void {
    if (!this.currentRectangle || !this.currentRectangle.startPoint) return;

    if (this.currentRectangle.layer) {
      this.map.removeLayer(this.currentRectangle.layer);
    }

    const startLat = this.currentRectangle.startPoint.lat;
    const startLng = this.currentRectangle.startPoint.lng;
    const endLat = mousePos.lat;
    const endLng = mousePos.lng;

    const bounds = [
      [Math.min(startLat, endLat), Math.min(startLng, endLng)],
      [Math.max(startLat, endLat), Math.max(startLng, endLng)]
    ];

    this.currentRectangle.layer = L.rectangle(bounds, {
      color: '#4CAF50',
      fillColor: '#4CAF50',
      fillOpacity: 0.3,
      weight: 2,
      dashArray: '5, 5'
    });

    this.currentRectangle.layer.addTo(this.map);
    this.currentRectangle.bounds = bounds;
  }

  private finishRectangle(): void {
    if (!this.currentRectangle || !this.currentRectangle.bounds) return;

    this.isDrawingRectangle = false;

    if (this.currentRectangle.layer) {
      this.map.removeLayer(this.currentRectangle.layer);
    }

    if (!this.isRectangleInsideAnyRegion(this.currentRectangle.bounds)) {
      const errorMessage = this.translate.instant('DRAW_ONLY_INSIDE_REGION');
      this.showDrawingError(errorMessage);
      this.currentRectangle = null;
      this.removeRectangleHelp();
      return;
    }

    const rectangleOptions = {
      stroke: true,
      color: '#2E7D32',
      weight: 3,
      opacity: 1,
      fill: true,
      fillColor: '#4CAF50',
      fillOpacity: 0.35
    };

    const rectangle = L.rectangle(this.currentRectangle.bounds, rectangleOptions);

    rectangle.addTo(this.drawnItems);

    this.shapesChanged.emit(this.getDrawnShapes());

    const bounds = this.currentRectangle.bounds;
    const centerLat = (bounds[0][0] + bounds[1][0]) / 2;
    const centerLng = (bounds[0][1] + bounds[1][1]) / 2;

    this.mapClick.emit({
      lat: centerLat,
      lng: centerLng,
      type: 'rectangle',
      bounds: bounds
    });

    this.currentRectangle = null;
    this.map.off('mousemove');
    this.removeRectangleHelp();
  }

  private cancelRectangleDrawing(): void {
    if (this.currentRectangle && this.currentRectangle.layer) {
      this.map.removeLayer(this.currentRectangle.layer);
    }
    this.currentRectangle = null;
    this.isDrawingRectangle = false;
    this.map.off('mousemove');
    this.removeRectangleHelp();
  }

  private enablePolylineDrawing(): void {
    this.map.off('click');
    this.map.off('dblclick');
    this.map.off('mousemove');

    this.disablePolygonInteractions();

    this.map.doubleClickZoom.disable();

    this.showPolylineHelp();

    this.map.getContainer().style.cursor = 'crosshair';

    this.map.on('click', (e: any) => {
      if (this.polylineDrawingEnabled) {
        this.addPolylinePoint(e.latlng);
      }
    });

    this.map.on('dblclick', (e: any) => {
      if (this.polylineDrawingEnabled && this.currentPolylinePoints.length >= 2) {
        L.DomEvent.stopPropagation(e);
        this.finishPolyline();
      }
    });

    this.map.on('mousemove', (e: any) => {
      if (this.polylineDrawingEnabled) {
        this.updateDrawingCursor(e.latlng);

        if (this.currentPolylinePoints.length > 0) {
          this.updatePolylinePreview(e.latlng);
        }
      }
    });
  }

  private showPolylineHelp(): void {
    const helpDiv = L.DomUtil.create('div', 'polyline-drawing-help');
    helpDiv.style.cssText = `
      position: absolute;
      top: 60px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(0, 0, 0, 0.9);
      color: white;
      padding: 12px 20px;
      border-radius: 6px;
      font-size: 14px;
      z-index: 1000;
      pointer-events: none;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    `;
    helpDiv.innerHTML = this.translate.instant('DRAW_POLYGON_HELP');

    this.map.getContainer().appendChild(helpDiv);

    (this as any).polylineHelpDiv = helpDiv;
  }

  private addPolylinePoint(latlng: any): void {

    if (this.currentPolylinePoints.length >= 3 && this.currentPolylineMarkers.length > 0) {
      const firstPoint = this.currentPolylinePoints[0];
      const distance = this.map.distance(latlng, firstPoint);

      if (distance < 10) {
        this.finishPolyline();
        return;
      }
    }

    this.currentPolylinePoints.push(latlng);

    const marker = L.circleMarker(latlng, {
      radius: this.currentPolylineMarkers.length === 0 ? 6 : 4,
      fillColor: '#4CAF50',
      fillOpacity: 1,
      color: '#fff',
      weight: 2
    }).addTo(this.map);

    if (this.currentPolylineMarkers.length === 0) {
      marker.on('mouseover', () => {
        if (this.currentPolylinePoints.length >= 3) {
          marker.setStyle({ radius: 8, fillColor: '#45a049' });
        }
      });
      marker.on('mouseout', () => {
        marker.setStyle({ radius: 6, fillColor: '#4CAF50' });
      });
    }

    this.currentPolylineMarkers.push(marker);

    if (this.currentPolylinePoints.length >= 2) {
      this.updatePolylineLines();
    }
  }

  private updatePolylineLines(): void {
    if (this.currentPolyline) {
      this.map.removeLayer(this.currentPolyline);
    }

    this.currentPolyline = L.polyline(this.currentPolylinePoints, {
      color: '#4CAF50',
      weight: 2,
      opacity: 0.8,
      dashArray: '5, 5'
    }).addTo(this.map);
  }

  private updatePolylinePreview(mousePos: any): void {
    if (this.currentPolylinePoints.length === 0) return;

    if (this.currentPolyline) {
      this.map.removeLayer(this.currentPolyline);
    }

    const previewPoints = [...this.currentPolylinePoints, mousePos];

    if (previewPoints.length >= 3) {
      this.currentPolyline = L.polygon(previewPoints, {
        color: '#4CAF50',
        fillColor: '#4CAF50',
        fillOpacity: 0.2,
        weight: 2,
        dashArray: '5, 5'
      }).addTo(this.map);
    } else {
      this.currentPolyline = L.polyline(previewPoints, {
        color: '#4CAF50',
        weight: 2,
        opacity: 0.8,
        dashArray: '5, 5'
      }).addTo(this.map);
    }
  }

  private finishPolyline(): void {
    if (this.currentPolylinePoints.length < 3) return;

    const polygonOptions = {
      stroke: true,
      color: '#2E7D32',
      weight: 3,
      opacity: 1,
      fill: true,
      fillColor: '#4CAF50',
      fillOpacity: 0.35
    };

    const polygon = L.polygon(this.currentPolylinePoints, polygonOptions);

    polygon.addTo(this.drawnItems);

    this.shapesChanged.emit(this.getDrawnShapes());

    const points = this.currentPolylinePoints.map(p => [p.lat, p.lng]);

    this.cancelCurrentPolyline();

    this.removeDrawingHelp();

    this.mapClick.emit({
      lat: points[0][0],
      lng: points[0][1],
      type: 'polygon',
      points: points
    });
  }

  private cancelCurrentPolyline(): void {
    this.currentPolylineMarkers.forEach(marker => {
      this.map.removeLayer(marker);
    });
    this.currentPolylineMarkers = [];

    if (this.currentPolyline) {
      this.map.removeLayer(this.currentPolyline);
      this.currentPolyline = null;
    }

    this.currentPolylinePoints = [];
  }

  private removeDrawingHelp(): void {
    if ((this as any).polylineHelpDiv) {
      this.map.getContainer().removeChild((this as any).polylineHelpDiv);
      (this as any).polylineHelpDiv = null;
    }
  }

  private applyGradientToPolygon(gradientId: string): void {
    setTimeout(() => {
      const mapContainer = this.map.getContainer();
      const svgs = mapContainer.querySelectorAll('svg');

      svgs.forEach((svg: SVGSVGElement) => {
        let defs = svg.querySelector('defs');
        if (!defs) {
          defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
          svg.insertBefore(defs, svg.firstChild);
        }

        if (!defs.querySelector(`#${gradientId}`)) {
          const gradient = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
          gradient.setAttribute('id', gradientId);
          gradient.setAttribute('x1', '0%');
          gradient.setAttribute('y1', '0%');
          gradient.setAttribute('x2', '100%');
          gradient.setAttribute('y2', '100%');

          const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
          stop1.setAttribute('offset', '0%');
          stop1.setAttribute('stop-color', '#4CAF50');
          stop1.setAttribute('stop-opacity', '0.4');
          gradient.appendChild(stop1);

          const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
          stop2.setAttribute('offset', '50%');
          stop2.setAttribute('stop-color', '#81C784');
          stop2.setAttribute('stop-opacity', '0.3');
          gradient.appendChild(stop2);

          const stop3 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
          stop3.setAttribute('offset', '100%');
          stop3.setAttribute('stop-color', '#A5D6A7');
          stop3.setAttribute('stop-opacity', '0.2');
          gradient.appendChild(stop3);

          defs.appendChild(gradient);
        }

        const polygon = svg.querySelector(`.polygon-${gradientId} path`) as SVGPathElement;
        if (polygon) {
          polygon.style.fill = `url(#${gradientId})`;
          polygon.style.fillOpacity = '1';
        }
      });
    }, 100);
  }

  private disableDrawing(): void {
    this.map.off('click');
    this.map.off('dblclick');
    this.map.off('mousemove');

    this.map.doubleClickZoom.enable();

    this.enablePolygonInteractions();

    this.map.on('click', (e: any) => {
      if (!this.rectangleDrawingEnabled && !this.polylineDrawingEnabled) {
        const coords = e.latlng;
        this.mapClick.emit({
          lat: coords.lat,
          lng: coords.lng
        });
        this.coordinatesChange.emit([coords.lat, coords.lng]);
      }
    });
  }

  public getDrawnShapes(): any[] {
    const shapes: any[] = [];
    if (this.drawnItems) {
      let index = 0;
      this.drawnItems.eachLayer((layer: any) => {
        const shape: any = {
          id: layer._leaflet_id || `shape_${index}`,
          type: this.getShapeType(layer),
          layer: layer
        };

        if (layer instanceof L.Rectangle || layer instanceof L.Polygon) {
          shape.bounds = layer.getBounds();
          shape.coordinates = layer.getLatLngs();

          if (layer instanceof L.Polygon) {
            const latLngs = layer.getLatLngs()[0];
            shape.points = latLngs.map((latlng: any) => [latlng.lat, latlng.lng]);
          }
        }

        shapes.push(shape);
        index++;
      });
    }
    return shapes;
  }

  private getShapeType(layer: any): string {
    if (layer instanceof L.Rectangle) {
      return 'rectangle';
    } else if (layer instanceof L.Polygon) {
      return 'polygon';
    }
    return 'unknown';
  }

  public deleteShape(shapeId: string | number): void {
    if (this.drawnItems) {
      this.drawnItems.eachLayer((layer: any) => {
        if (layer._leaflet_id === shapeId || `shape_${layer._leaflet_id}` === shapeId) {
          this.drawnItems.removeLayer(layer);
        }
      });
      this.shapesChanged.emit(this.getDrawnShapes());
    }
  }

  public clearAllShapes(): void {
    if (this.drawnItems) {
      this.drawnItems.clearLayers();
      this.shapesChanged.emit(this.getDrawnShapes());
    }
  }

  public toggleSatelliteLayer(): void {
    if (this.isSatelliteLayerVisible) {
      this.removeSatelliteLayer();
    } else {
      this.addSatelliteLayer();
    }
  }

  public setSatelliteOpacity(opacity: number): void {
    if (this.satelliteLayer) {
      this.satelliteLayer.setOpacity(opacity);
    }
  }

  public getSatelliteLayerVisible(): boolean {
    return this.isSatelliteLayerVisible;
  }

  public setMapLayer(layerType: string, layerNameOverride?: string): void {
    if (this.isSatelliteLayerVisible) {
      this.removeSatelliteLayer();
    }
    this.removeAllIndexLayers();

    switch(layerType) {
      case 'satellite':
        this.addSatelliteLayer();
        break;
      case 'ndvi':
        this.addIndexMockLayer('ndvi', layerNameOverride);
        break;
      case 'ndmi':
        this.addIndexMockLayer('ndmi', layerNameOverride);
        break;
      case 'gndvi':
        this.addIndexMockLayer('gndvi', layerNameOverride);
        break;
      case 'osavi':
        this.addIndexMockLayer('osavi', layerNameOverride);
        break;
      case 'mndwi':
        this.addIndexMockLayer('mndwi', layerNameOverride);
        break;
      case 'evi':
        this.addIndexMockLayer('evi', layerNameOverride);
        break;
      case 'orvi':
        this.addIndexMockLayer('orvi', layerNameOverride);
        break;
      case 'default':
        this.removeAllIndexLayers();
        break;
      default:
        break;
    }
  }

  private addIndexMockLayer(indexType: string, layerNameOverride?: string): void {
    const layerKey = layerNameOverride || indexType.toUpperCase();
    console.log(`🗺️ addIndexMockLayer: indexType=${indexType}, layerNameOverride=${layerNameOverride}, layerKey=${layerKey}`);

    if (this.indexLayers[layerKey]) {
      console.log(`🗺️ Слой ${layerKey} уже существует, добавляем на карту`);
      this.indexLayers[layerKey].addTo(this.map);
      return;
    }

    const tileUrl = `${environment.api}/tiles/Map/GetTile?lat={y}&lon={x}&z={z}&layer=${layerKey}&coordinateSystem=1`;
    console.log(`🗺️ Создаем новый слой с URL: ${tileUrl}`);

    const indexLayer = L.tileLayer(tileUrl, {
      attribution: `© ${layerKey} Index`,
      maxZoom: 19,
      opacity: 0.8,
      renderer: this.canvasRenderer
    });

    const layer = L.layerGroup([indexLayer]);
    this.indexLayers[layerKey] = layer;
    layer.addTo(this.map);
    this.isNdviLayerVisible = true;
    console.log(`✅ Слой ${layerKey} успешно добавлен на карту`);
  }

  private removeAllIndexLayers(): void {
    Object.keys(this.indexLayers).forEach(key => {
      const layer = this.indexLayers[key];
      if (this.map && layer) {
        this.map.removeLayer(layer);
        layer.eachLayer((subLayer: any) => {
          if (subLayer.remove) {
            subLayer.remove();
          }
        });
        layer.clearLayers();
      }
    });
    this.indexLayers = {};
    this.isNdviLayerVisible = false;
  }

  private addCustomToggleControl(): void {
    if (!this.map || !L) return;

    const CustomToggleControl = L.Control.extend({
      options: {
        position: 'bottomleft'
      },

      onAdd: () => {
        const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-custom-toggle');

        const schemaBtn = L.DomUtil.create('button', 'toggle-btn schema-btn', container);
        schemaBtn.innerHTML = this.translate.instant('MAP_VIEW');
        if (!this.isSatelliteLayerVisible) {
          L.DomUtil.addClass(schemaBtn, 'active');
        }

        const satelliteBtn = L.DomUtil.create('button', 'toggle-btn satellite-btn', container);
        satelliteBtn.innerHTML = this.translate.instant('SATELLITE_VIEW');
        if (this.isSatelliteLayerVisible) {
          L.DomUtil.addClass(satelliteBtn, 'active');
        }

        this.viewToggleButtons.schemaBtn = schemaBtn;
        this.viewToggleButtons.satelliteBtn = satelliteBtn;

        L.DomEvent.on(schemaBtn, 'click', (e: Event) => {
          L.DomEvent.stopPropagation(e);
          L.DomEvent.preventDefault(e);
          if (this.isSatelliteLayerVisible) {
            this.toggleSatelliteLayer();
            L.DomUtil.addClass(schemaBtn, 'active');
            L.DomUtil.removeClass(satelliteBtn, 'active');
          }
        });

        L.DomEvent.on(satelliteBtn, 'click', (e: Event) => {
          L.DomEvent.stopPropagation(e);
          L.DomEvent.preventDefault(e);
          if (!this.isSatelliteLayerVisible) {
            this.toggleSatelliteLayer();
            L.DomUtil.removeClass(schemaBtn, 'active');
            L.DomUtil.addClass(satelliteBtn, 'active');
          }
        });

        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        return container;
      }
    });

    new CustomToggleControl().addTo(this.map);
  }

  private addMonitoringPointsControl(): void {
    if (!this.map || !L) return;

    const MonitoringPointsControl = L.Control.extend({
      options: {
        position: 'topright'
      },

      onAdd: () => {
        const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-monitoring-points');

         const button = L.DomUtil.create('button', 'monitoring-points-btn', container);
        button.innerHTML = `<img src="assets/icons/main/light/point-elem.svg" width="24" height="24" alt="Monitoring Points">`;
        button.setAttribute('title', this.translate.instant('MONITORING_POINTS'));

        this.tooltipButtons.push({ element: button, key: 'MONITORING_POINTS' });

         L.DomEvent.on(button, 'click', (e: Event) => {
          L.DomEvent.stopPropagation(e);
          L.DomEvent.preventDefault(e);

           const rect = button.getBoundingClientRect();
          const position = {
            top: rect.top,
            right: window.innerWidth - rect.right,
            left: rect.left,
            bottom: rect.bottom
          };

          this.monitoringPointsClick.emit(position);
        });

        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        return container;
      }
    });

    new MonitoringPointsControl().addTo(this.map);
  }

  private addTopLeftControls(): void {
    if (!this.map || !L) return;

     const DrawingControls = L.Control.extend({
      options: {
        position: 'topleft'
      },

      onAdd: () => {
        const container = L.DomUtil.create('div', 'leaflet-control-drawing-group');

         const rectangleBtn = L.DomUtil.create('button', 'leaflet-control-button rectangle-btn', container);
        rectangleBtn.setAttribute('type', 'button');
        rectangleBtn.innerHTML = `
          <img src="assets/icons/main/light/square.svg" width="20" height="20" alt="Rectangle">
        `;
        rectangleBtn.setAttribute('title', this.translate.instant('Draw Rectangle'));
        rectangleBtn.style.pointerEvents = 'auto';
        rectangleBtn.style.cursor = 'pointer';

        this.tooltipButtons.push({ element: rectangleBtn, key: 'Draw Rectangle' });

        const polylineBtn = L.DomUtil.create('button', 'leaflet-control-button polyline-btn', container);
        polylineBtn.setAttribute('type', 'button');
        polylineBtn.innerHTML = `
          <img src="assets/icons/main/light/sixers.svg" width="20" height="20" alt="Polyline">
        `;
        polylineBtn.setAttribute('title', this.translate.instant('Draw Polygon'));
        polylineBtn.style.pointerEvents = 'auto';
        polylineBtn.style.cursor = 'pointer';

        this.tooltipButtons.push({ element: polylineBtn, key: 'Draw Polygon' });

        L.DomEvent.on(rectangleBtn, 'click', (e: Event) => {
          L.DomEvent.stopPropagation(e);
          L.DomEvent.preventDefault(e);
          this.toggleRectangleDrawing(rectangleBtn);
        });

        L.DomEvent.on(polylineBtn, 'click', (e: Event) => {
          L.DomEvent.stopPropagation(e);
          L.DomEvent.preventDefault(e);
          this.togglePolylineDrawing(polylineBtn);
        });

        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        return container;
      }
    });

    new DrawingControls().addTo(this.map);
  }

  private addSearchControl(): void {
    if (!this.map || !L) return;

     const SearchControl = L.Control.extend({
      options: {
        position: 'topright'
      },

      onAdd: () => {
        const container = L.DomUtil.create('div', 'leaflet-control-search');

         const searchContainer = L.DomUtil.create('div', 'search-input-container', container);

         L.DomEvent.disableClickPropagation(searchContainer);

        return container;
      }
    });

    new SearchControl().addTo(this.map);
  }

   public initDynamicPolygonsLayer(): void {
    if (!this.map || !L) return;

    if (!this.map.getPane('dynamicPolygonsPane')) {
      const polygonsPane = this.map.createPane('dynamicPolygonsPane');
      polygonsPane.style.zIndex = '950';
    }

    if (!this.map.getPane('regionBoundaryPane')) {
      const boundaryPane = this.map.createPane('regionBoundaryPane');
      boundaryPane.style.zIndex = '1300';
    }

    if (!this.dynamicPolygonsLayer) {
      this.dynamicPolygonsLayer = L.layerGroup({ pane: 'dynamicPolygonsPane' }).addTo(this.map);
    }

    if (!this.regionBoundaryLayer) {
      this.regionBoundaryLayer = L.layerGroup({ pane: 'regionBoundaryPane' }).addTo(this.map);
    }

    if (!this.regionHighlightLayer) {
      this.regionHighlightLayer = L.layerGroup().addTo(this.map);
    }
  }

  public addDynamicPolygon(coordinates: [number, number][], properties: any = {}): any {

    if (!this.dynamicPolygonsLayer) {
      this.initDynamicPolygonsLayer();
    }

    const isDrawingPolygons = this.drawingMode;

    const polygonOptions: any = {
      color: properties.color || '#3388ff',
      weight: properties.weight || 2,
      opacity: properties.opacity || 0.8,
      fillColor: properties.fillColor || properties.color || '#3388ff',
      fillOpacity: properties.fillOpacity !== undefined ? properties.fillOpacity : 0.3,
      fill: true,
      interactive: properties.interactive !== undefined ? properties.interactive : !isDrawingPolygons
    };

    if (properties.pane) {
      polygonOptions.pane = properties.pane;
    }

     if (properties.className) {
      polygonOptions.className = properties.className;
    }

    const polygon = L.polygon(coordinates, polygonOptions);

    if (properties.name && (properties.type === 'season' || properties.type === 'direct-season' || properties.type === 'season-field' || this.showRegionNames || properties.tooltipOptions)) {
      const tooltipContent = properties.tooltip || this.getTranslatedRegionName(properties);
      const tooltipOptions = properties.tooltipOptions ??
        {
          permanent: false,
          direction: 'center',
          className: properties.type?.includes('season') ? 'season-tooltip' : 'region-tooltip',
          opacity: 0.9,
          sticky: true
        };
      polygon.bindTooltip(tooltipContent, tooltipOptions);
    }

    polygon.on('mouseover', (e: any) => {
      const layer = e.target;
      if (properties.type?.includes('season')) {
        layer.setStyle({
          weight: 3,
          color: '#ffffff',
          dashArray: '',
          fillOpacity: 0.6
        });
      }
      layer.bringToFront();
    });

    polygon.on('mouseout', (e: any) => {
      const layer = e.target;
      if (properties.type?.includes('season')) {
        layer.setStyle({
          weight: polygonOptions.weight || 2,
          color: polygonOptions.color,
          fillOpacity: polygonOptions.fillOpacity || 0.4
        });
      }
    });

     polygon.on('click', (e: any) => {
       if (this.rectangleDrawingEnabled || this.polylineDrawingEnabled) {
         return;
       }

       const bounds = polygon.getBounds();
      const center = bounds.getCenter();

       this.mapDblClick.emit({
        lat: center.lat,
        lng: center.lng,
        polygonData: properties
      });

      L.DomEvent.stopPropagation(e);
    });

    if (properties.layer !== 'boundary') {
      this.allPolygons.push({ polygon, coordinates, properties });
    }

    if (properties.layer === 'boundary') {
      this.regionBoundaryLayer?.addLayer(polygon);
    } else if (properties.type === 'season' || properties.type === 'field' || properties.type === 'season-field' || properties.type === 'direct-season' ||
        properties.className === 'season-polygon' || properties.className === 'field-polygon') {
      this.dynamicPolygonsLayer?.addLayer(polygon);
    }

    if (properties.layer !== 'boundary' && !this.isBulkAddingPolygons) {
      this.updatePolygonVisibility();
    }

    return polygon;
  }

  public startBulkPolygonAdd(): void {
    this.isBulkAddingPolygons = true;
  }

  public finishBulkPolygonAdd(): void {
    this.isBulkAddingPolygons = false;
    this.updatePolygonVisibility();
  }

  public clearDynamicPolygons(): void {
    if (this.dynamicPolygonsLayer) {
      this.dynamicPolygonsLayer.clearLayers();
    }
    this.allPolygons = [];
    this.polygonClusters.clear();
    this.clearPolygonCountMarkers();
  }

  public clearRegionBoundaries(): void {
    if (this.regionBoundaryLayer) {
      this.regionBoundaryLayer.clearLayers();
    }
  }

  public getDynamicPolygonsCount(): number {
    return this.allPolygons.length;
  }

  public getAllPolygons(): any[] {
    return this.allPolygons;
  }

  public getCenter(): { lat: number, lng: number } | null {
    if (!this.map) return null;

    try {
      if (!this.map._loaded) {
        console.warn('Map not loaded when getting center');
        return null;
      }

      const center = this.map.getCenter();
      if (!center || center.lat === undefined || center.lng === undefined) {
        console.warn('Invalid center returned from map');
        return null;
      }

      return { lat: center.lat, lng: center.lng };
    } catch (error) {
      console.error('Error getting map center:', error);
      return null;
    }
  }

  public getZoom(): number | undefined {
    if (!this.map) return undefined;
    return this.map.getZoom();
  }

  private updatePolygonVisibility(): void {
    if (!this.dynamicPolygonsLayer || !this.map || !L) return;

    try {
      if (!this.map._loaded) {
        console.warn('Map not loaded for polygon visibility update');
        return;
      }

      const count = this.getDynamicPolygonsCount();
      const zoom = this.map.getZoom();

      if (zoom === undefined || zoom === null) {
        console.warn('Invalid zoom level for polygon visibility update');
        return;
      }

    this.clearPolygonCountMarkers();

    this.dynamicPolygonsLayer.eachLayer((layer: any) => {
      const polygon = this.allPolygons.find(p => p.polygon === layer);
      if (!polygon || (polygon.properties.type !== 'field' && polygon.properties.type !== 'season' &&
                      polygon.properties.className !== 'field-polygon' && polygon.properties.className !== 'season-polygon')) {
        this.dynamicPolygonsLayer.removeLayer(layer);
      }
    });

    const cropRegionPolygons = this.allPolygons.filter(p =>
      p.properties && (p.properties.isCropRegion || p.properties.id?.includes('crop-region') || p.properties.id?.includes('no-crop-region'))
    );
    const fieldSeasonPolygons = this.allPolygons.filter(p =>
      p.properties && (p.properties.type === 'field' || p.properties.type === 'season' || p.properties.type === 'season-field' || p.properties.type === 'direct-season' || p.properties.className === 'field-polygon' || p.properties.className === 'season-polygon')
    );
    const otherPolygons = this.allPolygons.filter(p =>
      !p.properties || (!p.properties.isCropRegion && !p.properties.id?.includes('crop-region') && !p.properties.id?.includes('no-crop-region') &&
                        p.properties.type !== 'field' && p.properties.type !== 'season' && p.properties.type !== 'season-field' && p.properties.type !== 'direct-season' &&
                        p.properties.className !== 'field-polygon' && p.properties.className !== 'season-polygon')
    );

    cropRegionPolygons.forEach(p => {
      p.polygon.addTo(this.dynamicPolygonsLayer);
    });

    if (zoom <= 6) {
      fieldSeasonPolygons.forEach(p => {
        if (p.properties && (p.properties.type === 'field' || p.properties.type === 'season-field') &&
            p.properties.type !== 'season' && p.properties.type !== 'direct-season') {
          this.dynamicPolygonsLayer.removeLayer(p.polygon);
        }
      });
    }

    const shouldClusterOthers = zoom < this.clusterZoomThreshold && otherPolygons.length > 1;

    if (shouldClusterOthers && otherPolygons.length > 0) {
      const originalPolygons = this.allPolygons;
      this.allPolygons = otherPolygons;
      this.showPolygonClusters();
      this.allPolygons = originalPolygons;
    } else {
      otherPolygons.forEach(p => {
        p.polygon.addTo(this.dynamicPolygonsLayer);
      });
    }

    this.updatePolygonTooltips();
    } catch (error) {
      console.error('Error in updatePolygonVisibility:', error);
    }
  }

  private showPolygonClusters(): void {
    if (!this.map || !L) return;

    try {
      if (!this.map._loaded) {
        console.warn('Map not loaded for polygon clustering');
        return;
      }

      const zoom = this.map.getZoom();
      const bounds = this.map.getBounds();

      if (!bounds || !zoom) {
        console.warn('Unable to get map bounds or zoom for clustering');
        return;
      }

    const gridSize = Math.max(3, Math.min(8, Math.floor(20 - zoom)));
    const latRange = bounds.getNorth() - bounds.getSouth();
    const lngRange = bounds.getEast() - bounds.getWest();
    const cellLat = latRange / gridSize;
    const cellLng = lngRange / gridSize;

    const clusters = new Map<string, {
      count: number;
      center: [number, number];
      polygons: any[];
      bounds: any;
    }>();

    this.allPolygons.forEach(polygonData => {
      const coords = polygonData.coordinates;

      let centerLat = 0, centerLng = 0;
      coords.forEach((coord: [number, number]) => {
        centerLat += coord[0];
        centerLng += coord[1];
      });
      centerLat /= coords.length;
      centerLng /= coords.length;

      if (bounds.contains([centerLat, centerLng])) {
        const gridX = Math.floor((centerLng - bounds.getWest()) / cellLng);
        const gridY = Math.floor((centerLat - bounds.getSouth()) / cellLat);

        if (gridX >= 0 && gridX < gridSize && gridY >= 0 && gridY < gridSize) {
          const key = `${gridX}_${gridY}`;

          if (!clusters.has(key)) {
            const cellCenterLat = bounds.getSouth() + (gridY + 0.5) * cellLat;
            const cellCenterLng = bounds.getWest() + (gridX + 0.5) * cellLng;
            clusters.set(key, {
              count: 0,
              center: [cellCenterLat, cellCenterLng],
              polygons: [],
              bounds: L.latLngBounds([[cellCenterLat - cellLat/2, cellCenterLng - cellLng/2],
                                     [cellCenterLat + cellLat/2, cellCenterLng + cellLng/2]])
            });
          }

          const cluster = clusters.get(key)!;
          cluster.count++;
          cluster.polygons.push(polygonData);
        }
      }
    });

    clusters.forEach((cluster, key) => {
      if (cluster.count === 1) {
        const polygonData = cluster.polygons[0];
        polygonData.polygon.addTo(this.dynamicPolygonsLayer);

        const polygon = polygonData.polygon;
        const properties = polygonData.properties;
        if (this.showRegionNames && properties.name) {
          if (!polygon.getTooltip()) {
            const translatedName = this.getTranslatedRegionName(properties);
            polygon.bindTooltip(translatedName, {
              permanent: false,
              direction: 'center',
              className: 'region-tooltip'
            });
          }
        } else {
          if (polygon.getTooltip()) {
            polygon.unbindTooltip();
          }
        }
      } else if (cluster.count > 1) {
        const size = Math.min(60, 30 + cluster.count * 2);
        const fontSize = Math.min(20, 14 + Math.floor(cluster.count / 5));

        const divIcon = L.divIcon({
          html: `<div style="
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border: 3px solid #fff;
            border-radius: 50%;
            width: ${size}px;
            height: ${size}px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: ${fontSize}px;
            color: #fff;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
            cursor: pointer;
            transition: all 0.3s ease;">
            ${cluster.count}
          </div>`,
          className: 'polygon-cluster-marker',
          iconSize: [size, size],
          iconAnchor: [size/2, size/2]
        });

        const marker = L.marker(cluster.center, { icon: divIcon });

        marker.on('click', () => {
          this.map.fitBounds(cluster.bounds, { padding: [50, 50] });
        });

        marker.on('mouseover', (e: any) => {
          const icon = e.target._icon;
          if (icon) {
            icon.style.transform = 'scale(1.1)';
          }
        });

        marker.on('mouseout', (e: any) => {
          const icon = e.target._icon;
          if (icon) {
            icon.style.transform = 'scale(1)';
          }
        });

        marker.addTo(this.map);
        this.polygonCountMarkers.push(marker);

        this.polygonClusters.set(key, cluster);
      }
    });
    } catch (error) {
      console.error('Error in showPolygonClusters:', error);
    }
  }

  private showPolygonCountMarkers(count: number): void {
    this.showPolygonClusters();
  }

  private clearPolygonCountMarkers(): void {
    this.polygonCountMarkers.forEach(marker => {
      if (this.map) {
        this.map.removeLayer(marker);
      }
    });
    this.polygonCountMarkers = [];
  }

  public setMaxVisiblePolygons(max: number): void {
    this.maxVisiblePolygons = max;
    this.updatePolygonVisibility();
  }

  public setClusterZoomThreshold(threshold: number): void {
    this.clusterZoomThreshold = threshold;
    this.updatePolygonVisibility();
  }

  private checkPolygonsOverlap(polygons: any[]): boolean {
    if (polygons.length < 2) return false;

    for (let i = 0; i < polygons.length - 1; i++) {
      const bounds1 = L.latLngBounds(polygons[i].coordinates);

      for (let j = i + 1; j < polygons.length; j++) {
        const bounds2 = L.latLngBounds(polygons[j].coordinates);

        if (bounds1.intersects(bounds2)) {
          return true;
        }
      }
    }

    return false;
  }

  public getMapBounds(): any {
    if (this.map) {
      const bounds = this.map.getBounds();
      return {
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest()
      };
    }
    return null;
  }

  public invalidateSize(): void {
    if (this.map) {
      this.map.invalidateSize();
    }
  }

  public getBounds(): any {
    if (this.map) {
      return this.map.getBounds();
    }
    return null;
  }

  public highlightRegionBoundary(coordinates: any, regionName?: string): void {
    if (!this.map || !L || !this.regionHighlightLayer) return;

    this.clearRegionHighlight();

    try {
      let polygonCoordinates: [number, number][] = [];

      if (Array.isArray(coordinates)) {
        polygonCoordinates = coordinates.map((coord: any) => {
          if (Array.isArray(coord) && coord.length >= 2) {
            return [coord[0], coord[1]] as [number, number];
          } else if (coord.lat && coord.lng) {
            return [coord.lat, coord.lng] as [number, number];
          } else if (coord.latitude && coord.longitude) {
            return [coord.latitude, coord.longitude] as [number, number];
          }
          return [0, 0] as [number, number];
        });
      } else if (coordinates.coordinates) {
        const processCoords = (coords: any): [number, number][] => {
          if (coords[0] && typeof coords[0][0] === 'number') {
            return coords.map((c: any) => [c[1], c[0]] as [number, number]);
          } else {
            return processCoords(coords[0]);
          }
        };
        polygonCoordinates = processCoords(coordinates.coordinates);
      }

      if (polygonCoordinates.length > 0) {
        const highlightPolygon = L.polygon(polygonCoordinates, {
          color: '#FF5722',
          weight: 4,
          opacity: 1,
          fillColor: 'transparent',
          fillOpacity: 0,
          dashArray: '10, 5'
        });

        highlightPolygon.addTo(this.regionHighlightLayer);
        this.currentHighlightedRegion = highlightPolygon;

        if (regionName) {
          highlightPolygon.bindTooltip(regionName, {
            permanent: false,
            direction: 'center',
            className: 'region-tooltip'
          });
        }
      }
    } catch (error) {
      console.error('Error highlighting region boundary:', error);
    }
  }

  public clearRegionHighlight(): void {
    if (this.regionHighlightLayer) {
      this.regionHighlightLayer.clearLayers();
    }
    this.currentHighlightedRegion = null;
  }

  private isPointInsideRegionBoundary(latlng: any): boolean {
    if (!this.selectedRegionBoundary) {
      return true;
    }

    try {
      const point = [latlng.lat, latlng.lng];

      let boundaryCoords: [number, number][] = [];

      if (Array.isArray(this.selectedRegionBoundary)) {
        if (this.selectedRegionBoundary.length > 0 && Array.isArray(this.selectedRegionBoundary[0]) && this.selectedRegionBoundary[0].length === 2) {
          boundaryCoords = this.selectedRegionBoundary.map((coord: any) => [coord[1], coord[0]]);
        } else {
          boundaryCoords = this.selectedRegionBoundary.map((coord: any) => [coord[0], coord[1]]);
        }
      } else if (this.selectedRegionBoundary.coordinates) {
        const coords = this.selectedRegionBoundary.coordinates;
        if (coords[0] && typeof coords[0][0] === 'number') {
          boundaryCoords = coords.map((c: any) => [c[1], c[0]]);
        } else {
          boundaryCoords = coords[0].map((c: any) => [c[1], c[0]]);
        }
      }

      return this.isPointInPolygon(point, boundaryCoords);
    } catch (error) {
      console.error('Error checking point in region boundary:', error);
      return true;
    }
  }

  private showRegionBoundaryWarning(): void {
    const warningDiv = L.DomUtil.create('div', 'region-boundary-warning');
    warningDiv.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(244, 67, 54, 0.95);
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;
      z-index: 10000;
      pointer-events: none;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    `;
    warningDiv.innerHTML = 'Drawing is only allowed inside the selected region boundary';

    this.map.getContainer().appendChild(warningDiv);

    setTimeout(() => {
      if (warningDiv.parentNode) {
        warningDiv.parentNode.removeChild(warningDiv);
      }
    }, 3000);
  }

  private updateDrawingCursor(latlng: any): void {
    this.map.getContainer().style.cursor = 'crosshair';
  }

  public refreshAllPolygonTooltips(): void {
    this.updatePolygonTooltips();
  }

  private updateDrawingConstraints(): void {
     if (this.polylineDrawingEnabled || this.rectangleDrawingEnabled) {
      this.map.getContainer().style.cursor = 'crosshair';
    }
  }

  private disablePolygonInteractions(): void {
     this.allPolygons?.forEach(polygonData => {
      if (polygonData?.polygon) {
         polygonData.polygon.options.interactive = false;
        if (polygonData.polygon._path) {
          polygonData.polygon._path.style.pointerEvents = 'none';
        }
      }
    });
  }

  private enablePolygonInteractions(): void {
     this.allPolygons?.forEach(polygonData => {
      if (polygonData?.polygon) {
         polygonData.polygon.options.interactive = true;
        if (polygonData.polygon._path) {
          polygonData.polygon._path.style.pointerEvents = 'auto';
        }
      }
    });
  }
}
